import WarningIcon from '@mui/icons-material/Warning';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import {
  MIN_GREATER_THAN_MAX_DURATION,
  NO_COMMON_LABWARE,
} from '../../constants';
import type { TimeConstraintErrorType } from '../../types';
import CustomWidthTooltip from '../CustomWidthTooltip';

const timeConstraintErrorCopy = {
  [NO_COMMON_LABWARE]: 'Tasks must share a common labware.',
  [MIN_GREATER_THAN_MAX_DURATION]:
    'Max duration must be greater than min duration.',
} satisfies Record<TimeConstraintErrorType, string>;

const TriangleBackdrop = styled(Box)`
  position: relative;
  display: inline-block;
  top: 0.75rem;
  width: 0;
  height: 0;
  border-left: 1.5rem solid transparent;
  border-right: 1.5rem solid transparent;
  border-bottom: ${(p) => `2.5rem solid ${p.theme.palette.grey[100]}`};
  color: ${(p) => p.theme.palette.error.main};
`;

export const TimeConstraintErrorIcon = ({
  errors,
}: {
  errors: TimeConstraintErrorType[];
}): JSX.Element => {
  const theme = useTheme();
  return (
    <CustomWidthTooltip
      title={
        <Stack direction="row">
          <Box alignContent="center" width="3rem">
            <WarningAmberIcon htmlColor={theme.palette.error.main} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              component="h2"
              sx={{ textTransform: 'uppercase' }}
            >
              Invalid time constraint
            </Typography>
            <List sx={{ listStyleType: 'disc' }}>
              {errors.map((errorType) => (
                <ListItem key={errorType} sx={{ display: 'list-item' }}>
                  <Typography variant="subtitle2" component="p">
                    {timeConstraintErrorCopy[errorType]}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        </Stack>
      }
      placement="right"
    >
      <TriangleBackdrop>
        <WarningIcon
          fontSize="large"
          htmlColor={theme.palette.error.main}
          sx={{
            transform: 'translate(-50%, 0.3rem)',
          }}
        />
      </TriangleBackdrop>
    </CustomWidthTooltip>
  );
};
